import { ForwardedRef, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingPortal } from '@floating-ui/react';
import { Label, Listbox, ListboxOptions, Transition } from '@headlessui/react';

import { classNames } from 'support/helpers/generic/generic';
import { forwardRefWithGeneric } from 'support/helpers/hocs/forwardRefWithGeneric';

import { deserializeValue, getControlledValue, getSelectedOptions, serializeValue } from './utils';
import { DropdownButton } from './components/DropdownButton';
import { DropdownOptions } from './components/DropdownOptions';
import { DropdownSearchOption } from './components/DropdownSearchOption';
import { ListboxHandlingOpenState } from './components/ListboxHandlingOpenState';
import { dropdownClasses } from './styles';
import { DropdownType } from './types';
import { useDropdownFloating } from './useDropdownFloating';

/**
 * Dropdown component with generic type support and conditional value types
 * based on the multiple prop.
 */
function DropdownComponent<TValue>(
  {
    label,
    description,
    required,
    emptySelectionLabel,
    options = [],
    name,
    value,
    onChange,
    onFocus,
    onBlur,
    onOpen,
    onClose,
    errors,
    fieldState,
    disabled,
    className,
    buttonClassName,
    wrapperClassName,
    id,
    showSelection = true,
    leftIcon,
    rightIcon,
    variant = 'default',
    size = 'base',
    dropdownDisplayTestId,
    multiple,
    searchEnabled,
  }: DropdownType<TValue>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const resolvedFieldState = fieldState ?? (errors ? 'error' : 'default');
  // Use our custom hook for floating UI functionality
  const {
    wrapperRef,
    floatingRefs,
    floatingStyles,
    floatingWidth,
    getReferenceProps,
    getFloatingProps,
    createBlurHandler,
  } = useDropdownFloating();

  // Create a blur handler that checks if focus is moving to floating element
  const handleOnBlur = createBlurHandler(onBlur);

  // Determine if this is a multiple selection dropdown
  const isMultipleSelection = Boolean(multiple);

  // Get selected options and controlled value using utility functions
  const selectedOptions = getSelectedOptions(options, value, isMultipleSelection);
  const controlledValue = getControlledValue(value, isMultipleSelection);
  const isLarge = variant === 'large';
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));

  const handleOnClose = () => {
    onClose?.();
    setSearch(''); // Reset search when dropdown closes
  };
  return (
    <div ref={wrapperRef} className={wrapperClassName} id={id}>
      {/* Hidden input to store the actual value */}
      <input
        ref={ref}
        type="text"
        value={serializeValue(controlledValue, isMultipleSelection)}
        name={name}
        aria-label={name}
        className="hidden"
        onChange={(e) => {
          const deserializedValue = deserializeValue<TValue>(e.target.value, isMultipleSelection);
          onChange?.(deserializedValue as any);
        }}
      />

      <Listbox
        value={controlledValue || ''}
        onChange={onChange as any}
        disabled={disabled}
        multiple={isMultipleSelection}
      >
        {({ open }) => {
          return (
            <ListboxHandlingOpenState open={open} onOpen={onOpen} onClose={handleOnClose}>
              <div className={className}>
                {(label || description) && isLarge ? (
                  <div className="mb-2 flex flex-col gap-1">
                    {label ? (
                      <Label className="block text-wrap text-base font-semibold text-gray-900">
                        {t(label)}
                        {required && <span className="text-procuros-green-600"> *</span>}
                      </Label>
                    ) : null}
                    {description && <p className="text-sm text-gray-700">{description}</p>}
                  </div>
                ) : label ? (
                  <Label className="mb-1 block text-wrap text-sm text-gray-500">
                    {t(label)}
                    {required && <span className="text-procuros-green-600">*</span>}
                  </Label>
                ) : null}

                <div className="relative flex">
                  <DropdownButton
                    selectedOptions={selectedOptions}
                    emptySelectionLabel={emptySelectionLabel}
                    showSelection={showSelection}
                    size={size}
                    buttonClassName={buttonClassName}
                    fieldState={resolvedFieldState}
                    open={open}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    disabled={disabled}
                    name={name}
                    referenceProps={getReferenceProps({
                      ref: floatingRefs.setReference,
                    })}
                    onBlur={handleOnBlur}
                    onFocus={onFocus}
                    displayTestId={dropdownDisplayTestId}
                  />

                  <FloatingPortal>
                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions
                        as="ul"
                        static
                        className={classNames(
                          dropdownClasses.list.size[size],
                          'w-full z-50 bg-white shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 overflow-auto focus-visible:outline-none',
                        )}
                        ref={floatingRefs.setFloating}
                        style={{ ...floatingStyles, width: `${floatingWidth}px` }}
                        {...getFloatingProps()}
                      >
                        {searchEnabled ? (
                          <DropdownSearchOption search={search} setSearch={setSearch} size={size} />
                        ) : null}
                        <DropdownOptions options={filteredOptions} size={size} />
                      </ListboxOptions>
                    </Transition>
                  </FloatingPortal>
                </div>

                {Boolean(errors) && <p className="mt-2 text-sm text-red-600">{errors?.message}</p>}
                {Boolean(description) && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
              </div>
            </ListboxHandlingOpenState>
          );
        }}
      </Listbox>
    </div>
  );
}

export const Dropdown = forwardRefWithGeneric(DropdownComponent);
